/**
 * Shared Gemini structured-output schemas for physical continuity extraction.
 * ARCHIVIST_INITIAL and PHYSICAL_EXTRACTION_CORRECTION must use these definitions.
 */

export const PHYSICAL_SCHEMA_TYPE = {
  STRING: 'string',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  OBJECT: 'object',
  ARRAY: 'array',
} as const;

export type PhysicalSchemaObject = {
  type: string;
  description?: string;
  properties?: Record<string, PhysicalSchemaObject>;
  items?: PhysicalSchemaObject;
  required?: string[];
};

export function buildContinuityObjectItemSchema(): PhysicalSchemaObject {
  return {
    type: PHYSICAL_SCHEMA_TYPE.OBJECT,
    description: 'Structured physical continuity object linked to a manifest candidate.',
    properties: {
      source_candidate_id: {
        type: PHYSICAL_SCHEMA_TYPE.STRING,
        description: 'Candidate ID from the physical candidate manifest (required).',
      },
      objectId: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      canonicalName: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      objectType: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      ownerCharacterIds: {
        type: PHYSICAL_SCHEMA_TYPE.ARRAY,
        items: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      },
      currentHolderCharacterId: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      currentLocationDescription: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      operationalStatus: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      mobilityStatus: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      continuityImportance: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      unresolved: { type: PHYSICAL_SCHEMA_TYPE.BOOLEAN },
      unresolvedObligation: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      aliases: {
        type: PHYSICAL_SCHEMA_TYPE.ARRAY,
        items: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      },
      state: {
        type: PHYSICAL_SCHEMA_TYPE.STRING,
        description: 'Optional prose state detail merged into operationalStatus when needed.',
      },
    },
    required: ['source_candidate_id', 'canonicalName', 'objectType', 'operationalStatus'],
  };
}

export function buildPhysicalObligationItemSchema(): PhysicalSchemaObject {
  return {
    type: PHYSICAL_SCHEMA_TYPE.OBJECT,
    description: 'Open physical continuity obligation for a structured object.',
    properties: {
      source_candidate_id: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      obligationId: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      objectId: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      originatingChapter: { type: PHYSICAL_SCHEMA_TYPE.NUMBER },
      status: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      obligation: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      description: { type: PHYSICAL_SCHEMA_TYPE.STRING },
      requiredByChapter: { type: PHYSICAL_SCHEMA_TYPE.NUMBER },
    },
    required: ['objectId', 'description', 'status', 'obligation'],
  };
}

export function buildPhysicalCorrectionResponseSchema(): PhysicalSchemaObject {
  return {
    type: PHYSICAL_SCHEMA_TYPE.OBJECT,
    properties: {
      continuity_objects: {
        type: PHYSICAL_SCHEMA_TYPE.ARRAY,
        items: buildContinuityObjectItemSchema(),
      },
      physical_continuity_obligations: {
        type: PHYSICAL_SCHEMA_TYPE.ARRAY,
        items: buildPhysicalObligationItemSchema(),
      },
    },
    required: ['continuity_objects', 'physical_continuity_obligations'],
  };
}

export function buildArchivistInitialPhysicalArrayProperties(): Record<string, PhysicalSchemaObject> {
  return {
    continuity_objects: {
      type: PHYSICAL_SCHEMA_TYPE.ARRAY,
      items: buildContinuityObjectItemSchema(),
    },
    physical_continuity_obligations: {
      type: PHYSICAL_SCHEMA_TYPE.ARRAY,
      items: buildPhysicalObligationItemSchema(),
    },
  };
}
